import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from "axios";
import { toast } from 'react-toastify';
import { FaEdit } from 'react-icons/fa';
import { TbSortAscending2Filled } from "react-icons/tb";
import { BsFiletypeCsv } from "react-icons/bs";

import Spinner from '../../components/spinner';
import AddedBy from "../../components/addedBy";
import { useLanguage } from '../../context/languageContext';
import GenerateCSV from '../../components/popup/generateCSV';
import FilterBar from '../../components/filterBar';
import SortBy from '../../components/sortBy';
import { useFilterChips } from '../../components/filterBar/useFilterChips';
import Pagination from '../../components/pagination';
import NoContent from '../../components/notFound/noBooks';
import { formatMongoDBTime } from '../../utills/dateAndTime';
import { normalizeCoverImage, convertToCloudFrontUrl , fetchAllGenresByLangOptions , fetchAllActorNames } from '../../helper';
import { Can } from "../../context/permissionContext";
import { joinList } from "../../helper/listSeparator";

function Collections() {
    const { adminInfo, isShowCSVModalVisible, setIsShowCSVModalVisible, handleCloseCSV, handleGenerateCSV } = useLanguage();
    const apiParamsRef = useRef(null);

    const navigate = useNavigate();
    // The router's location, not window.location — see useFilterChips.
    const location = useLocation();

    const [loading, setLoading] = useState(false);
    const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);
    const [selectAllCollections, setSelectAllCollections] = useState(false);
    const [collectionsData, setCollectionsData] = useState(null);


    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [totalNumOfCollections, setTotalNumOfCollections] = useState(0);

    // Search state

    // Sorting state
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
    // The Sort by control. Sent on every request, so it is the list's order
    // of record; the column arrow below keeps itself in step with it.
    const [sortBy, setSortBy] = useState('latestCreated');

    const totalPages = Math.ceil(totalNumOfCollections / itemsPerPage);


    // The chip row under the search box. `chipFilters` is already in the

    // shape the endpoint reads, so it merges straight into `apiFilters`.

    const filters = useFilterChips({ supportsMatchAny: true, stickyField: 'collectionName' });

    const apiFilters = {
        currentPage,
        itemsPerPage,
        langCode: "en",
        sortOrder: sortOrder,
        sortBy,
    // LAST on purpose: the single-field search above writes '' into every
    // field it is not searching, so a spread placed above it is wiped.
    ...filters.chipFilters,
    matchMode: filters.matchMode,
    };


  // Genre is a managed list, so the filter offers the stored names rather than
  // asking anyone to spell them. Fetched once and shared by the Genre chip.
  const [genreNames, setGenreNames] = useState([]);

  useEffect(() => {
    if (!adminInfo?.token) return;
    let cancelled = false;
    fetchAllGenresByLangOptions('en', adminInfo.token).then((genres) => {
      if (!cancelled) setGenreNames(genres.map((g) => g.genreName));
    });
    return () => { cancelled = true; };
  }, [adminInfo?.token]);


  // The names behind Created by / Edited by, so those filters are picked from
  // the team rather than spelled from memory.
  const [actorNames, setActorNames] = useState([]);

  useEffect(() => {
    if (!adminInfo?.token) return;
    let cancelled = false;
    fetchAllActorNames(adminInfo.token).then((names) => {
      if (!cancelled) setActorNames(names);
    });
    return () => { cancelled = true; };
  }, [adminInfo?.token]);

    const filterOptions = [
        { value: 'collectionName', label: 'Journey Name' },
        { value: 'bookName', label: 'Books' },
        { value: 'genreName', label: 'Genre', suggestions: genreNames },
        { value: 'createdAt', label: 'Created on' },
        { value: 'updatedAt', label: 'Updated on' },
        { value: 'createdBy', label: 'Created by', suggestions: actorNames },
        { value: 'updatedBy', label: 'Edited by', suggestions: actorNames }
    ];

    useEffect(() => {
        setCurrentPage(1);
    }, [itemsPerPage, sortOrder, sortBy, filters.signature]);

    useEffect(() => {
        if (!adminInfo?.token) {
            return;
        }

        document.title = "Penspace - Journeys";
        const keyValues = JSON.stringify({ currentPage, itemsPerPage, sortOrder, sortBy, filters: filters.signature });

        if (apiParamsRef.current === keyValues) {
            return;
        }

        apiParamsRef.current = keyValues;
        fetchAllCollectionsData();
    }, [adminInfo, currentPage, itemsPerPage, sortOrder, sortBy, filters.signature]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
    };


    const handleSort = () => {
        setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
        // Keeps the arrow live now that Sort by decides the order.
        setSortBy(prev => (prev === 'oldestCreated' ? 'latestCreated' : 'oldestCreated'));
    };

    const fetchAllCollectionsData = async () => {
        setLoading(true);

        try {
            const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };
            const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/collection/getall`, { filters: apiFilters }, { headers });

            if (response.status) {
                const collectionsData = response?.data || [];
                // Normalize coverImage for all collections to handle legacy formats
                const normalizedCollections = collectionsData.collectionsDocs?.map(collection => ({
                    ...collection,
                    coverImage: normalizeCoverImage(collection.coverImage)
                })) || [];
                setCollectionsData(normalizedCollections);
                setTotalNumOfCollections(collectionsData.totalCount || 0);
            }
        } catch (error) {
            console.error("fetchAllCollectionsData catch error::", error)
        } finally {
            setTimeout(() => setLoading(false), 300);
        }
    };

    const handleSelectCollectionId = (seletedRowId) => {
        if (selectedCollectionIds.includes(seletedRowId)) {
            setSelectedCollectionIds(prevState => prevState.filter(productId => productId !== seletedRowId));
        } else {
            setSelectedCollectionIds(prevState => [...prevState, seletedRowId]);
        }
    };

    const handleSelectAllCollections = () => {
        if (selectAllCollections) {
            setSelectedCollectionIds([]);
        } else {
            setSelectedCollectionIds(collectionsData?.map((value) => value._id));
        }
        setSelectAllCollections((prevSelectAll) => !prevSelectAll);
    };

    const handleDeleteRows = async () => {
        try {
            const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };
            const { data: response } = await axios.put(`${process.env.REACT_APP_API_ROOT}/api/admin/collection/delete`, { selectedCollectionIds }, { headers });

            if (response.status === true) {
                setSelectedCollectionIds([]);
                setSelectAllCollections(false);
                fetchAllCollectionsData();
                toast.success(response.message || "Journeys moved to the trash.");
            }
        } catch (error) {
            console.error("handleDeleteRows catch error::", error);
        }
    };

    // Editing happens on its own route now — a grid of book covers does not fit
    // a dialog. The list refetches on mount, so coming back shows the change.
    // The list URL travels along, so the editor returns to it with its filters.
    const returnState = { state: { returnTo: `${location.pathname}${location.search}` } };
    const handleAddShowCollection = () => navigate("/collections/new", returnState);

    const handleEditShowCollection = (catId) => navigate(`/collections/${catId}/edit`, returnState);


    return (
        <React.Fragment>
            <div className="heading">
                <h4>
                    {totalNumOfCollections > 0 ? `Journeys (${totalNumOfCollections})` : `Journeys`}
                </h4>
                <div className="d-flex gap-2">
                    {selectedCollectionIds.length > 0 ? (
                        <Can feature="collections" action="delete"><button type="button" className="btn btn-outline-danger" onClick={handleDeleteRows}>Delete</button></Can>
                    ) : (
                        <div className="d-flex gap-2">
                            <button type="button" className="btn btn-outline-dark" onClick={() => setIsShowCSVModalVisible(true)}>Generate CSV <BsFiletypeCsv /></button>
                            <Can feature="collections" action="create"><button type="button" className="btn btn-dark" onClick={handleAddShowCollection}>Add Journey</button></Can>
                        </div>
                    )}
                </div>
            </div>
            <div className='d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center my-1'>
              <SortBy value={sortBy} onChange={setSortBy} />
                {totalPages > 1 && (
                    <div className='d-flex justify-content-center justify-content-lg-end w-100 w-lg-auto ms-lg-auto'>
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            totalItems={totalNumOfCollections}
                            itemsPerPage={itemsPerPage}
                            onPageChange={handlePageChange}
                            onItemsPerPageChange={handleItemsPerPageChange}
                            showItemsPerPage={true}
                            showTotalItems={true}
                        />
                    </div>
                )}
            </div>
            <FilterBar
              supportsMatchAny
            options={filterOptions}
            chips={filters.chips}
            matchMode={filters.matchMode}
            onMatchModeChange={filters.setMatchMode}
            onAdd={filters.addChip}
            onUpdate={filters.updateChip}
            onRemove={filters.removeChip}
            onClear={filters.clearChips}
            />

            {loading ? (
                <Spinner />
            ) : (
                <div className="table_scrollbar table-responsive mt-1">
                    <table className="table">
                        <thead>
                            <tr>
                                <th className="checkBox">
                                    <input
                                        className="box form-check-input"
                                        type="checkbox"
                                        checked={selectAllCollections}
                                        onChange={handleSelectAllCollections}
                                    />
                                </th>
                                <th>S.no</th>
                                <th>Cover</th>
                                <th>Journey Name
                                    <TbSortAscending2Filled
                                        size={18}
                                        style={{ cursor: 'pointer', marginLeft: '3px' }}
                                        onClick={handleSort}
                                        title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                                    />
                                </th>
                                <th>Books</th>
                                <th>Authors</th>
                                <th>Genre</th>
                                <th>Description</th>
                                <th>Created on</th>
                                <th>Updated on</th>
                                <th>Added by</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {collectionsData?.length > 0 ? (
                                collectionsData.map((collection, index) => {
                                    const isSelectedCollectionValues = selectedCollectionIds.some((selectedId) => selectedId === collection._id);
                                    const showSelectedLanguage = collection.translations;
                                    const globalIndex = (currentPage - 1) * itemsPerPage + index;

                                    return (
                                        (
                                            <tr key={collection._id}>
                                                <td className="checkBox">
                                                    <input
                                                        className="box form-check-input"
                                                        type="checkbox"
                                                        checked={isSelectedCollectionValues}
                                                        onChange={() => handleSelectCollectionId(collection._id)}
                                                    />
                                                </td>
                                                <td className="index">{globalIndex + 1}</td>
                                                <td className="image">
                                                    {collection.coverImage?.uploadedImageUrl ? (
                                                        <img src={convertToCloudFrontUrl(collection.coverImage.uploadedImageUrl)} alt="" size={24} />
                                                    ) : (
                                                        <p className="image-skeleton" aria-hidden="true"><span>NO COVER</span></p>
                                                    )}
                                                </td>
                                                <td className="max_width">{showSelectedLanguage.title}</td>
                                                <td className="max_width">
                                                    {showSelectedLanguage.books?.map(item => (
                                                        <li className="list_styled" key={item.bookId}>{item.bookName}</li>
                                                    ))}
                                                </td>
                                                {/* A book is never shown without its author: one line per
                                                    book, level with the Books column beside it. */}
                                                <td className="max_width">
                                                    {showSelectedLanguage.books?.map(item => (
                                                        <li className="list_styled" key={item.bookId}>
                                                            {item.authorNames?.length ? joinList(item.authorNames) : "—"}
                                                        </li>
                                                    ))}
                                                </td>
                                                <td className="max_width">
                                                    {showSelectedLanguage.genres?.map(item => (
                                                        <li className="list_styled" key={item.genreId}>{item.genreName}</li>
                                                    ))}
                                                </td>
                                                <td className="max_width">{showSelectedLanguage.description}</td>
                                                <td className="min_width">{formatMongoDBTime(collection.createdAt)}</td>
                                                <td className="min_width">{collection.updatedAt ? formatMongoDBTime(collection.updatedAt) : "—"}</td>
                                                <td><AddedBy record={collection} /></td>
                                                <td className="action">
                                                    <div onClick={() => handleEditShowCollection(collection._id)}><FaEdit size={20} /></div>
                                                </td>
                                            </tr>
                                        )
                                    );
                                })
                            ) : (
                                <NoContent title="journeys" colSpan={12} />
                            )}
                        </tbody>
                    </table>
                </div>
            )}


            <GenerateCSV
                title="journeys"
                isShowCSVModalVisible={isShowCSVModalVisible}
                handleCloseCSV={handleCloseCSV}
                handleGenerateCSV={() => handleGenerateCSV("journeys", apiFilters)}
            />
        </React.Fragment>
    );
}

export default Collections
